import { ActionCard, Radio, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode } from 'react'
import {
  EnrichedBankAccountDetails,
  EnrichedCardDetails,
  isSavedBankAccountValid,
  isSavedCard,
  isSavedCardValid,
  PaymentMethodType,
  SelectedPaymentMethod,
  Features,
} from '../../model'
import {
  useApplePayFeature,
  useApplePayForCashCohort,
  useApplePayPayment,
  useAppRouter,
  useCashAppPay,
  useCohort,
  useIsRecoverableFlow,
  useSavedBankAccounts,
  useSavedCards,
  useSelectedFeature,
  useSelectedPaymentType,
  useShouldShowApplePayBanner,
} from '../../state'
import { PaymentMethodApplePay } from '../PaymentMethodApplePay'
import { PaymentMethodBankAccount } from '../PaymentMethodBankAccount'
import { PaymentMethodCard } from '../PaymentMethodCard'
import { useTranslation } from '../../utils/convergence'
import { PaymentMethodCashAppPay } from '../PaymentMethodCashAppPay'
import { Feature, PaymentType } from '@afterpay/types'
import { Cohort } from '@afterpaytouch/portal-api/types'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useHasPaymentMethods } from './hooks'
import { useIsApplePaylater, useIsApplePaylaterWithApplePayRepayment } from '../../state/applePaylater/hooks'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { Route } from '../../config/router'
import { TrackingEvent, PaymentMethodTypes } from '../../model/monitoring'
import { useApplePayTooltip, useApplePayTooltipClassic } from '../PaymentMethodApplePay/useApplePayTooltip'
import { useFlag } from '@afterpaytouch/feature'

interface PaymentMethodListProps {
  onChange: (method: SelectedPaymentMethod) => void
  current?: SelectedPaymentMethod
  labelledBy?: string
  addNewPaymentMethodDisabled: boolean
}

const getDefaultSelection = (
  current: SelectedPaymentMethod | undefined,
  savedCards: EnrichedCardDetails[],
  savedBankAccounts: EnrichedBankAccountDetails[] | undefined
): string | undefined => {
  switch (current?.type) {
    case PaymentMethodType.APPLE_PAY:
      return PaymentMethodType.APPLE_PAY
    case PaymentMethodType.CASH_APP_PAY:
      return 'cashAppPay' in current && current.cashAppPay.id ? current.cashAppPay.id.toString() : undefined
    case PaymentMethodType.ACH_BANK_ACCOUNT:
      return 'account' in current && isSavedBankAccountValid(current.account, savedBankAccounts) ? current.account.id.toString() : undefined
    case PaymentMethodType.CREDIT_CARD:
      if ('card' in current && isSavedCard(current.card) && isSavedCardValid(current.card, savedCards)) {
        return current.card.token
      }
  }
}

export const PaymentMethodList: FunctionComponent<PaymentMethodListProps> = ({ onChange, current, labelledBy = '', addNewPaymentMethodDisabled }) => {
  const router = useAppRouter()
  const applePayEnabled = useApplePayPayment()
  const isApplePaylater = useIsApplePaylater()
  const applePayFeature = useApplePayFeature()
  const savedCards = useSavedCards()
  const savedBankAccounts = useSavedBankAccounts()
  const sortedSavedBankAccounts =
    savedBankAccounts != null && savedBankAccounts !== undefined
      ? [...savedBankAccounts].sort((a, b) => {
          return (a.fraudCheckAchDisallowed ? 1 : 0) - (b.fraudCheckAchDisallowed ? 1 : 0)
        })
      : undefined
  const cashAppPay = useCashAppPay()
  const hasPaymentMethods = useHasPaymentMethods()
  const defaultSelectionToken = getDefaultSelection(current, savedCards, savedBankAccounts)
  const isRecoverableFlow = useIsRecoverableFlow()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [shouldShowApplePayBanner, saveApplePayBannerTag] = useShouldShowApplePayBanner()
  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI
  const { toggleTooltipClassic, applePayTooltipRendererClassic, closeTooltip } = useApplePayTooltipClassic()
  const { tooltipTriggerRef, openTooltip } = useApplePayTooltip(-10)
  const cashPayMonthlyEnabled = useFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED)
  const isApplePayEnabledForCashCohort = useApplePayForCashCohort()
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly
  const feature = useSelectedFeature()
  const isApplePayLaterApplePayRepaymentEnabled = useIsApplePaylaterWithApplePayRepayment()
  const showApplePayLaterApplePayRepaymentTopActionCard = feature === Feature.PCL && isApplePayLaterApplePayRepaymentEnabled
  const actionCardPosition = !showApplePayLaterApplePayRepaymentTopActionCard && isBronteDesignLanguage && hasPaymentMethods ? 'Bottom' : 'Default'
  const id = 'apple-pay-learn-more-tooltip'

  const handleNewPaymentMethodClick = (): void => {
    logEvent(TrackingEvent.PRESSED_PAYMENT_METHOD_NEW_CARD, () => {
      router.push(Route.NEW_PAYMENT_METHOD)
    })
  }

  const renderSavedCards = (): ReactNode[] => {
    return savedCards.map((card, index) => {
      return [
        <Radio.Button
          disabled={card.error !== null}
          key={`payment-method-option-${card.token}`}
          label={() => <PaymentMethodCard errorKey={card.error} reversed={!isArcadeDesignLanguage} card={card} />}
          labelPlacement='Middle'
          labelWidth='Full'
          onClick={(e) => handlePaymentMethodSelection(e, PaymentMethodType.CREDIT_CARD)}
          value={card.token}
          testNameSpace={`payment-method-option`}
          roundedBorder={isArcadeDesignLanguage}
        />,
      ]
    })
  }

  const renderCashAppPay = (): ReactNode[] => {
    return cashAppPay.map((cashAppPayItem) => {
      return [
        <Radio.Button
          key={`payment-method-option-${cashAppPayItem.id}`}
          disabled={(selectedPaymentType === PaymentType.PCL && !cashPayMonthlyEnabled) || cashAppPayItem.status !== 'ACTIVE'}
          label={() => (
            <PaymentMethodCashAppPay reversed={!isArcadeDesignLanguage} cashtag={cashAppPayItem.cashtag} validatePaymentType status={cashAppPayItem.status} />
          )}
          labelPlacement='Middle'
          labelWidth='Full'
          onClick={(e) => handlePaymentMethodSelection(e, PaymentMethodType.CASH_APP_PAY)}
          value={cashAppPayItem.id.toString()}
          roundedBorder={isArcadeDesignLanguage}
        />,
      ]
    })
  }

  const renderSavedBankAccounts = (): ReactNode[] | undefined => {
    return sortedSavedBankAccounts?.map((account, index) => {
      return [
        <Radio.Button
          disabled={account.error !== null || account.fraudCheckAchDisallowed}
          key={`payment-method-option-${index}`}
          label={() => (
            <PaymentMethodBankAccount
              {...(!isRecoverableFlow && { errorKey: account.error, disabled: account.fraudCheckAchDisallowed })}
              {...(isRecoverableFlow && { disabled: account.error !== null || account.fraudCheckAchDisallowed })}
              reversed={!isArcadeDesignLanguage}
              account={account}
            />
          )}
          labelPlacement='Middle'
          labelWidth='Full'
          onClick={(e) => handlePaymentMethodSelection(e, PaymentMethodType.ACH_BANK_ACCOUNT)}
          value={account.id.toString()}
          roundedBorder={isArcadeDesignLanguage}
        />,
      ]
    })
  }
  const renderApplePay = (): ReactNode => {
    if ((!applePayEnabled && !(isCashOnlyCohort && isApplePayEnabledForCashCohort)) || isApplePaylater) {
      return null
    }
    return [
      <Radio.Button
        itemRef={isArcadeDesignLanguage ? tooltipTriggerRef : undefined}
        labelWidth='Full'
        label={() => (
          <PaymentMethodApplePay
            showLearnMore={shouldShowApplePayBanner}
            handleToggleTooltip={isArcadeDesignLanguage ? openTooltip : toggleTooltipClassic}
            validatePaymentType
            iconSize='Medium'
            reversed={!isArcadeDesignLanguage}
            ariaDescribedBy={id}
          />
        )}
        value='APPLE_PAY'
        testNameSpace='payment-method-applepay-radio'
        key='payment-method-option-apple-pay'
        labelPlacement='Middle'
        disabled={!applePayFeature.enabledForConsumerLending}
        onClick={(e) => {
          if (!isArcadeDesignLanguage) {
            closeTooltip()
          }
          handlePaymentMethodSelection(e, PaymentMethodType.APPLE_PAY)
          if (shouldShowApplePayBanner) {
            saveApplePayBannerTag()
          }
        }}
        roundedBorder={isArcadeDesignLanguage}
      />,
      <>{!isArcadeDesignLanguage && applePayTooltipRendererClassic()}</>,
    ]
  }

  const handlePaymentMethodSelection = (e, type: PaymentMethodType): void => {
    let payload
    if (type === PaymentMethodType.APPLE_PAY) {
      payload = { type }
    } else if (type === PaymentMethodType.CASH_APP_PAY) {
      payload = { type, cashAppPay: cashAppPay.find((saved) => saved.id === Number(e.target.value)) }
    } else if (type === PaymentMethodType.ACH_BANK_ACCOUNT) {
      payload = { account: savedBankAccounts?.find((saved) => saved.id === Number(e.target.value)), type }
    } else {
      payload = { card: { token: e.target.value }, type }
    }
    logEvent(TrackingEvent.SELECTED_PAYMENT_METHOD, { paymentMethodType: PaymentMethodTypes[type] })
    onChange(payload)
  }

  const renderAddNewPaymentMethodCard = (): ReactNode => {
    const label = showApplePayLaterApplePayRepaymentTopActionCard ? t('paymentMethod:addNewDebitCard') : t('paymentMethod:addNewPaymentMethod')
    return (
      <ActionCard
        label={label}
        iconName='Add'
        onClick={handleNewPaymentMethodClick}
        position={actionCardPosition}
        disabled={addNewPaymentMethodDisabled}
        testNameSpace='add-new-payment-method'
      />
    )
  }

  return (
    <MaskedElement actionName='Select Payment Method'>
      <Radio.Group
        name='card'
        indent
        icon={isArcadeDesignLanguage ? undefined : 'Select'}
        kind='Option'
        defaultValue={defaultSelectionToken}
        aria-labelledby={labelledBy}
        reversed={isArcadeDesignLanguage}
      >
        {showApplePayLaterApplePayRepaymentTopActionCard && (
          <Radio.Header testNameSpace='add-new-payment-method'>{renderAddNewPaymentMethodCard()}</Radio.Header>
        )}
        {renderCashAppPay()}
        {renderSavedCards()}
        {renderSavedBankAccounts()}
        {renderApplePay()}
        {!showApplePayLaterApplePayRepaymentTopActionCard && <Radio.Footer>{renderAddNewPaymentMethodCard()}</Radio.Footer>}
      </Radio.Group>
    </MaskedElement>
  )
}
